import React from 'react';
import { Menu, MenuItem, Typography, Box, ListItemIcon, Divider } from '@mui/material';
import { FilterOption } from './UserProfileFilters';
import { roleDefinitions } from '../../roleDefinitions';
import { AvailableRoles } from '../../types';

interface FilterMenuProps {
  anchorEl: null | HTMLElement;
  onClose: () => void;
  filterOptions: FilterOption[];
  activeFilterTypes: string[];
  onSelect: (filterId: string, additionalProps?: Record<string, any>) => void;
  availableRoleTypes?: string[];
}

const FilterMenu: React.FC<FilterMenuProps> = ({ 
  anchorEl, 
  onClose, 
  filterOptions, 
  activeFilterTypes,
  onSelect,
  availableRoleTypes
}) => {
  const open = Boolean(anchorEl);

  // Use availableRoleTypes if provided, otherwise use all role types
  // Cast to AvailableRoles to fix type error
  const roleTypes = (availableRoleTypes || Object.keys(roleDefinitions)) as AvailableRoles[];
  
  // Filter out options that are already in use for status and date filters
  const availableOptions = filterOptions.filter(option => {
    // Always allow roles to be added multiple times
    if (option.id === 'roles') return true;
    
    // For status and date filters, only show if not already active
    return !activeFilterTypes.includes(option.id);
  });
  
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      {/* Regular filters */}
      {availableOptions.filter(option => option.id !== 'roles').map(option => (
        <MenuItem 
          key={option.id} 
          onClick={() => {
            onSelect(option.id);
            onClose();
          }}
        >
          {option.label}
        </MenuItem>
      ))}
      
      {/* Role filters - only show if 'roles' is available */}
      {availableOptions.some(option => option.id === 'roles') && roleTypes.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Role Filters
            </Typography>
          </Box>
          
          {roleTypes.map(roleType => (
            <MenuItem 
              key={roleType} 
              onClick={() => {
                onSelect('roles', { roleType });
                onClose();
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {React.createElement(roleDefinitions[roleType as AvailableRoles].roleDescriptions[0].icon, { 
                  fontSize: 'small',
                  sx: { fontSize: '1.25rem' }
                })}
              </ListItemIcon>
              {roleDefinitions[roleType as AvailableRoles].name}
            </MenuItem>
          ))}
        </>
      )}
    </Menu>
  );
};

export default FilterMenu; 